import { useCallback } from 'react';
import { lt, showErrorAlert, TimeUnitUtils, useLocalizeText, useTimezone } from '@mews/framework';
import { createHandler } from '@mews/std';
import { TimeUnit } from '@mews/enums';
import { TextKey } from '@mews-commander/enums';
import { getServiceTimeUnitPluralKey } from '@mews-commander/legacy-entities/src/utils/serviceTimeUnitLocalization';
import { useServiceInfo } from '@mews-commander/legacy-entities/src/utils/useServiceInfo';
import { useCommanderSelector } from '@mews-commander/utils';

const MAX_DAYS = 369;
const MAX_HOURS = 369;
const MAX_MONTHS = 59;

const getMaxReservationLength = createHandler<TimeUnit, number>({
    [TimeUnit.Hour]: MAX_HOURS,
    [TimeUnit.Day]: MAX_DAYS,
    [TimeUnit.Month]: MAX_MONTHS,
});

export const useIsReservationExceededMaxLength = (serviceId: string) => {
    const { timeUnit } = useServiceInfo(serviceId);
    const timezone = useTimezone();
    const timeUnitPluralKey = useCommanderSelector((state) => getServiceTimeUnitPluralKey(state, { id: serviceId }));
    const ltx = useLocalizeText();

    return useCallback(
        (interval: { startUtc: string; endUtc: string } | null, showError?: boolean) => {
            if (!timeUnit || !interval) {
                return false;
            }
            const { startUtc, endUtc } = interval;
            const timePart = TimeUnitUtils.timeUnitToDateTimePart(timeUnit);
            const timeUnitCount = TimeUnitUtils.getTimeUnitDiff(startUtc, endUtc, timezone, timePart);
            const maxLength = getMaxReservationLength(timeUnit);
            const exceeded = timeUnitCount > maxLength;

            const errorTextKey = TextKey.IntervalTooLongErrorMessage;
            const substitutes = {
                maxLength: `${maxLength + 1} ${
                    timeUnitPluralKey ? ltx({ textKey: timeUnitPluralKey }).toLocaleLowerCase() : ''
                }`,
            };

            if (showError && exceeded) {
                showErrorAlert({
                    description: lt(errorTextKey, substitutes),
                });
            }

            return exceeded ? ltx({ textKey: errorTextKey, substitutes }) : false;
        },
        [ltx, timeUnit, timeUnitPluralKey, timezone]
    );
};
